import axios from 'axios';
import { apiClient } from '@cyberint/common-utils';
import { FORCE_LOGOUT_EVENT } from '../constants/session-events';

const NOT_AUTHORIZED = 401;
const MAX_UNAUTHORIZED_COUNT = 3;
const WINDOW_MILLIS = 10000;
const AUTH_URLS = ['/login', '/logout', '/infinity_login'];

let timestamps = [];
let triggered = false;
let installed = false;

// Any 401 outside the auth endpoints counts as a dead-session signal. Services differ in
// their 401 bodies ("Invalid token", "Unauthorized", empty), so matching on the message
// would miss most of them; the MAX_UNAUTHORIZED_COUNT/WINDOW_MILLIS throttle is what
// protects against one-off 401s (e.g. a misconfigured integration) forcing a logout.
const isDeadSessionError = error => {
  if (error?.response?.status !== NOT_AUTHORIZED) return false;
  // Requests that handle their own 401 (e.g. the session-timeout dialog /me polling) opt out
  if (error?.config?.skipUnauthorizedInterceptor) return false;
  const url = error?.config?.url || '';
  return !AUTH_URLS.some(authUrl => url.includes(authUrl));
};

// Called by SessionTimeoutDialogBase on mount (i.e. after every login) so a future
// dead session can trigger again
export const resetUnauthorizedThrottle = () => {
  timestamps = [];
  triggered = false;
};

const onResponseError = error => {
  if (triggered || !isDeadSessionError(error)) return;

  const now = Date.now();
  timestamps = [...timestamps.filter(ts => now - ts < WINDOW_MILLIS), now];

  if (timestamps.length >= MAX_UNAUTHORIZED_COUNT) {
    triggered = true;
    window.dispatchEvent(new CustomEvent(FORCE_LOGOUT_EVENT, { detail: { reason: 'unauthorized' } }));
  }
};

/**
 * Watches 401 responses across the global axios instance and all
 * @cyberint/common-utils clients. When MAX_UNAUTHORIZED_COUNT such responses land within
 * WINDOW_MILLIS, dispatches FORCE_LOGOUT_EVENT once (latched) - SessionTimeoutDialogBase
 * listens and shows the expired-session modal. Errors are always re-thrown so callers
 * keep their own error handling. Install once at app startup.
 *
 * extraClients: axios instances not covered by the defaults (e.g. apps/ui's own
 * GDPR-prefixed clients from lib/http).
 *
 * Idempotent - a second call is a no-op. Without this, double-registering
 * onResponseError would count every 401 twice, halving the effective throttle.
 */
export const installUnauthorizedInterceptor = (extraClients = []) => {
  if (installed) return;
  installed = true;
  [axios, ...extraClients].forEach(client =>
    client.interceptors.response.use(
      response => response,
      error => {
        onResponseError(error);
        return Promise.reject(error);
      }
    )
  );
  apiClient.registerUnauthorizedHandler(onResponseError);
};
